import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import { FormField } from '@/components/forms/FormField'
import { computeDemandDetailKpis } from '@/features/demand/demandDetailMetrics'
import {
  demandService,
  type AssemblyType,
  type DemandBatchDetail,
  type IstaLevel,
} from '@/services/demand'
import { explosionService } from '@/services/explosions'
import { productService, type Product } from '@/services/products'
import { variantService, type ProductVariant } from '@/services/variants'

const inputClass =
  'w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 py-1 text-xs outline-none focus:border-[var(--color-accent)]'

type VariantOption = ProductVariant & { productCode: string }

type LineFormState = {
  variant_id: string
  quantity: string
  assembly_type: '' | AssemblyType
  ista_level: '' | IstaLevel
  notes: string
}

const EMPTY_LINE_FORM: LineFormState = {
  variant_id: '',
  quantity: '1',
  assembly_type: '',
  ista_level: '',
  notes: '',
}

export function DemandDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [batch, setBatch] = useState<DemandBatchDetail | null>(null)
  const [variants, setVariants] = useState<VariantOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)

  const [form, setForm] = useState<LineFormState>(EMPTY_LINE_FORM)
  const [editingId, setEditingId] = useState<string | null>(null)

  const variantLabel = useMemo(() => {
    const map = new Map(
      variants.map((v) => [v.id, `${v.variantCode} (${v.productCode})`]),
    )
    return (vid: string) => map.get(vid) ?? vid
  }, [variants])

  const reload = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const [detail, products] = await Promise.all([
        demandService.getById(id),
        productService.list({ pageSize: 500 }),
      ])
      if (!detail) {
        setBatch(null)
        setError('Đợt không tồn tại')
        return
      }
      setBatch(detail)
      const allVariants: VariantOption[] = []
      await Promise.all(
        products.items.map(async (p: Product) => {
          const rows = await variantService.listByProduct(p.id)
          for (const v of rows) {
            if (v.status === 'active') {
              allVariants.push({ ...v, productCode: p.code })
            }
          }
        }),
      )
      allVariants.sort((a, b) =>
        a.variantCode.localeCompare(b.variantCode, 'vi'),
      )
      setVariants(allVariants)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được đợt')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void reload()
  }, [reload])

  async function handleAddOrUpdate(e: React.FormEvent) {
    e.preventDefault()
    if (!batch) return
    if (!form.variant_id) {
      setError('Variant bắt buộc.')
      return
    }
    if (!form.assembly_type) {
      setError('Chế độ lắp ráp (Ráp chết hoặc Tháo rời) bắt buộc — không có mặc định.')
      return
    }
    if (!form.ista_level) {
      setError('Tiêu chuẩn đóng gói (ISTA 1A hoặc 3A) bắt buộc — không có mặc định.')
      return
    }
    const qty = Number(form.quantity)
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('quantity phải > 0.')
      return
    }
    const payload = {
      variant_id: form.variant_id,
      quantity: qty,
      assembly_type: form.assembly_type,
      ista_level: form.ista_level,
      notes: form.notes.trim() || null,
    }
    setSaving(true)
    setError(null)
    try {
      if (editingId) {
        await demandService.updateLine(batch.id, editingId, payload)
      } else {
        await demandService.createLine(batch.id, payload)
      }
      setForm(EMPTY_LINE_FORM)
      setEditingId(null)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lưu dòng thất bại')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(lineId: string) {
    if (!batch) return
    if (!window.confirm('Xóa dòng nhu cầu này?')) return
    setSaving(true)
    setError(null)
    try {
      await demandService.deleteLine(batch.id, lineId)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Xóa thất bại')
    } finally {
      setSaving(false)
    }
  }

  function startEdit(lineId: string) {
    const line = batch?.lines.find((l) => l.id === lineId)
    if (!line) return
    setEditingId(lineId)
    setForm({
      variant_id: line.variant_id,
      quantity: String(line.quantity),
      assembly_type: line.assembly_type,
      ista_level: line.ista_level,
      notes: line.notes || '',
    })
  }

  async function handleRunExplosion() {
    if (!batch) return
    if (batch.lines.length === 0) {
      setError('Đợt chưa có dòng — không thể chạy Explosion.')
      return
    }
    setRunning(true)
    setError(null)
    try {
      const run = await explosionService.run({ batch_id: batch.id })
      navigate(`/purchasing/explosions/${run.id}`)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Chạy Gross Explosion thất bại',
      )
    } finally {
      setRunning(false)
    }
  }

  const kpis = useMemo(
    () => computeDemandDetailKpis(batch?.lines ?? []),
    [batch],
  )

  if (loading) {
    return <PageLoading label="Đang tải đợt…" />
  }

  if (!batch) {
    return (
      <PageNotFound
        title="Không tìm thấy đợt"
        description={error ?? 'Đợt không tồn tại'}
        action={
          <Button
            variant="secondary"
            onClick={() => navigate('/purchasing/demand')}
          >
            Về danh sách
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Nhu cầu (Demand)', to: '/purchasing/demand' },
          { label: batch.title },
        ]}
      />
      <PageHeader
        title={batch.title}
        subtitle={`Tạo ${new Date(batch.created_at).toLocaleString('vi-VN')}${
          batch.created_by ? ` · ${batch.created_by}` : ''
        }`}
        actions={
          <Button
            disabled={running || batch.lines.length === 0}
            onClick={() => void handleRunExplosion()}
          >
            {running ? 'Đang chạy…' : 'Chạy Gross Explosion từ đợt này'}
          </Button>
        }
      />

      {batch.notes ? (
        <p className="mb-3 text-xs text-slate-600">{batch.notes}</p>
      ) : null}

      {error ? <PageError message={error} /> : null}

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Dòng" value={kpis.lineCount} />
        <KpiCard label="Tổng SL" value={kpis.totalQty} />
        <KpiCard label="Variant" value={kpis.uniqueVariants} />
        <KpiCard label="Phương án DG" value={kpis.packagingPairs} />
      </div>

      {/* Hand table: ProtoExcelTable would conflict with Sửa/Xóa + edit form */}
      <div className="mb-3 overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
        <table className="min-w-full text-left text-xs">
          <thead className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-slate-50 text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="sticky left-0 z-20 bg-slate-50 px-2 py-1.5 text-center">STT</th>
              <th className="sticky left-0 z-20 bg-slate-50 px-2 py-1.5">
                Variant
              </th>
              <th className="px-2 py-1.5">SL</th>
              <th className="px-2 py-1.5">Chế độ</th>
              <th className="px-2 py-1.5">ISTA</th>
              <th className="px-2 py-1.5">Notes</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {batch.lines.map((line, index) => (
              <tr
                key={line.id}
                className="border-t border-[var(--color-border)]"
              >
                <td className="sticky left-0 z-10 bg-[var(--color-surface)] px-2 py-1.5 text-center tabular-nums">
                  {index + 1}
                </td>
                <td className="sticky left-12 z-10 bg-[var(--color-surface)] px-2 py-1.5 font-mono">
                  {variantLabel(line.variant_id)}
                </td>
                <td className="px-2 py-1.5 tabular-nums">{line.quantity}</td>
                <td className="px-2 py-1.5 font-medium">
                  {line.assembly_type === 'assembled'
                    ? 'RÁP CHẾT (FA)'
                    : 'Tháo rời (KD)'}
                </td>
                <td className="px-2 py-1.5 font-mono">
                  ISTA {line.ista_level}
                </td>
                <td className="px-2 py-1.5 text-slate-500">
                  {line.notes || '—'}
                </td>
                <td className="space-x-2 px-2 py-1.5 text-right">
                  <button
                    type="button"
                    className="text-[var(--color-link)] hover:underline"
                    onClick={() => startEdit(line.id)}
                  >
                    Sửa
                  </button>
                  <button
                    type="button"
                    className="text-red-600 hover:underline"
                    disabled={saving}
                    onClick={() => void handleDelete(line.id)}
                  >
                    Xóa
                  </button>
                </td>
              </tr>
            ))}
            {batch.lines.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-2 py-4 text-center text-slate-400"
                >
                  Chưa có dòng nhu cầu.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={(e) => void handleAddOrUpdate(e)}
        className="grid gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-slate-50 p-3 shadow-[var(--shadow-level-1)] sm:grid-cols-2 lg:grid-cols-5"
      >
        <FormField
          label={editingId ? 'Sửa Variant' : 'Thêm Variant'}
          required
        >
          <select
            required
            className={inputClass}
            value={form.variant_id}
            onChange={(e) => setForm({ ...form, variant_id: e.target.value })}
          >
            <option value="">— Chọn Variant —</option>
            {variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.variantCode} ({v.productCode})
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Số lượng" required>
          <input
            required
            type="number"
            step="any"
            min="0.0001"
            className={inputClass}
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          />
        </FormField>
        <FormField label="Chế độ lắp ráp" required>
          <select
            required
            className={inputClass}
            value={form.assembly_type}
            onChange={(e) =>
              setForm({
                ...form,
                assembly_type: e.target.value as '' | AssemblyType,
              })
            }
          >
            <option value="">— Chọn chế độ —</option>
            <option value="assembled">Ráp chết (assembled)</option>
            <option value="kd">Tháo rời (kd)</option>
          </select>
        </FormField>
        <FormField label="Tiêu chuẩn ISTA" required>
          <select
            required
            className={inputClass}
            value={form.ista_level}
            onChange={(e) =>
              setForm({
                ...form,
                ista_level: e.target.value as '' | IstaLevel,
              })
            }
          >
            <option value="">— Chọn ISTA —</option>
            <option value="1A">ISTA 1A</option>
            <option value="3A">ISTA 3A</option>
          </select>
        </FormField>
        <FormField label="Notes">
          <input
            className={inputClass}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </FormField>
        <div className="sm:col-span-2 lg:col-span-5 flex justify-end gap-2">
          {editingId ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditingId(null)
                setForm(EMPTY_LINE_FORM)
              }}
            >
              Hủy sửa
            </Button>
          ) : null}
          <Button type="submit" size="sm" disabled={saving}>
            {editingId ? 'Cập nhật dòng' : 'Thêm dòng'}
          </Button>
        </div>
      </form>
    </div>
  )
}
